'use client'

import { useEffect, useRef, useState } from 'react'

import { trackCustomEvent } from '@/hooks/client/use-analytics-engine'
import { EventType } from '@/lib/analytics/events'
import { tw } from '@/lib/utils'

export interface FinderChipOption {
  key: string
  /** Need label, translated server-side (e.g. "Severe or profound loss"). */
  title: string
  releaseSlug: string
  /** Picked family name — proper noun. */
  name: string
}

interface FinderChipsProps {
  options: FinderChipOption[]
  /** "Our pick:" label, translated server-side. */
  ourPickLabel: string
  /** "{{name}} has been highlighted below" template, translated server-side.
   *  Shown as a mobile toast — on phones the reordered lineup is below the
   *  fold, so selecting a chip has no visible effect without it. */
  highlightToastTemplate: string
}

const TOAST_MS = 2600

const HIGHLIGHT_CLASSES = ['ring-2', 'ring-lapis', 'ring-offset-2', 'ring-offset-cream']

// The interactive half of the brand finder: need chips that highlight the
// recommended family in the (server-rendered) lineup grid below and float it
// to the front. The grid stays untouched server output — this island only
// writes CSS `order` + a highlight ring onto the `[data-lineup-family]`
// wrappers, so with JS disabled the chips are inert labels and the lineup
// keeps its editorial order.
export const FinderChips = ({ options, ourPickLabel, highlightToastTemplate }: FinderChipsProps) => {
  const [selected, setSelected] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (toastTimer.current !== null) {
        window.clearTimeout(toastTimer.current)
      }
    },
    []
  )

  const showToast = (name: string) => {
    if (toastTimer.current !== null) {
      window.clearTimeout(toastTimer.current)
    }
    setToast(highlightToastTemplate.replace('{{name}}', name))
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS)
  }

  useEffect(() => {
    const grid = document.querySelector('[data-brand-lineup-grid]')
    if (!grid) {
      return undefined
    }
    const items = Array.from(grid.querySelectorAll<HTMLElement>('[data-lineup-family]'))
    const pick = selected ? options.find((o) => o.key === selected) : null
    items.forEach((item, index) => {
      const isPick = !!pick && item.dataset.lineupFamily === pick.releaseSlug
      item.style.order = pick ? (isPick ? '0' : String(index + 1)) : ''
      item.classList[isPick ? 'add' : 'remove'](...HIGHLIGHT_CLASSES)
    })
    return () => {
      items.forEach((item) => {
        item.style.order = ''
        item.classList.remove(...HIGHLIGHT_CLASSES)
      })
    }
  }, [selected, options])

  const select = (key: string) => {
    const next = selected === key ? null : key
    setSelected(next)
    if (next) {
      trackCustomEvent('brand-finder--pick', EventType.Click, { position: key })
      const option = options.find((o) => o.key === next)
      if (option) {
        showToast(option.name)
      }
    } else {
      setToast(null)
    }
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {/* Mobile-only toast: on desktop the highlighted card is in view, so the
          confirmation would be noise. */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-4 bottom-6 z-50 flex justify-center lg:hidden"
        >
          <span className="bg-navy rounded-full px-5 py-2.5 text-center text-sm font-medium text-white shadow-xl">
            {toast}
          </span>
        </div>
      )}
      {options.map((option) => {
        const active = option.key === selected
        return (
          <button
            key={option.key}
            type="button"
            aria-pressed={active}
            data-tracking="ignore"
            onClick={() => select(option.key)}
            // min-w-0 so the pick line below can actually truncate: a grid item
            // defaults to min-width:auto and would grow to fit the name instead.
            className={tw(
              'flex min-w-0 cursor-pointer flex-col justify-between gap-2.5 rounded-2xl border bg-white p-4 text-left transition-all',
              active ? 'border-lapis shadow-lapis/10 shadow-md' : 'border-navy/10 hover:border-lapis/40 hover:shadow-sm'
            )}
          >
            <span className="flex items-start justify-between gap-2">
              <span className="text-navy text-md leading-snug font-semibold">{option.title}</span>
              <span
                aria-hidden
                className={tw(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs leading-none transition-colors',
                  active ? 'border-lapis bg-lapis text-white' : 'border-navy/20 text-transparent'
                )}
              >
                ✓
              </span>
            </span>
            {/* One line, always. A long family name ("Custom Made by ReSound")
                used to wrap and make its card taller than its neighbours, which
                skews the whole row — the title carries the meaning here, so the
                name ellipsises rather than distorting the grid. `title` keeps
                the full name reachable on hover. */}
            <span className="text-navy/50 block truncate text-sm leading-snug" title={option.name}>
              {ourPickLabel} <span className="text-lapis font-semibold">{option.name}</span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
